import React, {useEffect, useState, useCallback, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {Carousel} from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";
import "./PeekCarousel.css";
import {ReactComponent as IconArrowFoward} from "../../../../../assets/systemIcons/ic_arrow_foward.svg";
import {ReactComponent as IconArrowBack} from "../../../../../assets/systemIcons/ic_arrow_back.svg";
import { trackClick, getElementName } from "../../../../../utils/analytics/analytics";
import { ANALYTICS_MODULES, ANALYTICS_SECTIONS, ANALYTICS_ELEMENT_TYPES } from "../../../../../constants/analyticsConstants";

export default function PeekCarousel({slides = []}) {
    const navigate = useNavigate();
    const [embla, setEmbla] = useState(null);
    const [active, setActive] = useState(0);

    const autoplay = useRef(
        Autoplay({delay: 4000, stopOnInteraction: false})
    );

    const updateActive = useCallback(() => {
        if (!embla) return;
        setActive(embla.selectedScrollSnap());
    }, [embla]);

    // arrancar y mantener autoplay vivo
    useEffect(() => {
        if (!embla) return;
        autoplay.current?.play();
        updateActive();

        const onReInit = () => {
            autoplay.current?.reset(); // o play()
            updateActive();
        };

        embla.on("select", updateActive);
        embla.on("reInit", onReInit);

        // (opcional) si embla emite 'resize' en tu versión
        embla.on?.("resize", onReInit);

        return () => {
            embla?.off?.("select", updateActive);
            embla?.off?.("reInit", onReInit);
        };
    }, [embla, updateActive]);

    // si cambia orientación/resize de la ventana, reInit y reanudar
    useEffect(() => {
        const onResize = () => {
            embla?.reInit();
            autoplay.current?.reset();
        };
        window.addEventListener("orientationchange", onResize);
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("orientationchange", onResize);
            window.removeEventListener("resize", onResize);
        };
    }, [embla]);

    // si la imagen carga después, reInit y reanudar
    const handleImgLoad = () => {
        embla?.reInit();
        autoplay.current?.reset();
    };

    const scrollTo = (i) => embla && embla.scrollTo(i);

    const getStateClass = (i) => {
        if (slides.length <= 1) return "peekCarousel__slide--active";
        const prev = (active - 1 + slides.length) % slides.length;
        const next = (active + 1) % slides.length;
        if (i === active) return "peekCarousel__slide--active";
        if (i === prev || i === next) return "peekCarousel__slide--adjacent";
        return "peekCarousel__slide--far";
    };

    return (
        <Carousel
            getEmblaApi={setEmbla}
            loop
            align="center"
            slideGap="sm"
            slidesToScroll={1}
            includeGapInSize={false}
            withIndicators
            plugins={[autoplay.current]}
            onMouseEnter={autoplay.current.stop}
            onMouseLeave={autoplay.current.reset}
            nextControlIcon={<IconArrowFoward size={34} fill="rgba(0, 0, 0, 0.50)"/>}
            previousControlIcon={<IconArrowBack size={34} fill="rgba(0, 0, 0, 0.50)"/>}
            classNames={{
                viewport: "peekCarousel__viewport",
                container: "peekCarousel__container",
                controls: "peekCarousel__controls",
                slide: "peekCarousel__slide",
                control: "peekCarousel__control",
                indicator: "peekCarousel__indicator",
                indicators: "peekCarousel__indicators",
            }}
        >
            {slides.map((slide, i) => (
                <Carousel.Slide key={i}>
                    <div
                        className={`peekCarousel__imageContainer ${getStateClass(i)}`}
                        onClick={() => {
                        if (i === active) {
                            trackClick({
                                module: ANALYTICS_MODULES.LANDING,
                                section: ANALYTICS_SECTIONS.LANDING_CAROUSEL,
                                elementName: getElementName(ANALYTICS_ELEMENT_TYPES.CARD, ANALYTICS_SECTIONS.LANDING_CAROUSEL, 'slide_active'),
                                elementType: ANALYTICS_ELEMENT_TYPES.CARD
                            });
                            navigate("/productos");
                        } else {
                            trackClick({
                                module: ANALYTICS_MODULES.LANDING,
                                section: ANALYTICS_SECTIONS.LANDING_CAROUSEL,
                                elementName: getElementName(ANALYTICS_ELEMENT_TYPES.CARD, ANALYTICS_SECTIONS.LANDING_CAROUSEL, 'slide_inactive'),
                                elementType: ANALYTICS_ELEMENT_TYPES.CARD
                            });
                            scrollTo(i);
                        }
                    }}
                    >
                        <div
                            className="peekCarousel__bodyContainer"
                        >
                            <h2>{slide.title}</h2>
                            <div className="peekCarousel__footerContainer">
                                <p>{slide.body}</p>
                                <button className="peekCarousel__footerButton">
                                    {slide.buttonText}
                                </button>
                            </div>
                        </div>
                        <img
                            src={slide.image}
                            alt={slide.title}
                            className={`peekCarousel__img ${slide.imgClass || ""}`}
                            draggable="false"
                            onLoad={handleImgLoad}
                        />

                    </div>
                </Carousel.Slide>
            ))}
        </Carousel>
    );
}
